import { defineComponent, withDirectives, resolveDirective, h } from 'vue';
import utils from '../../Shared/utils.jsx';
import BaseComponent from './BaseButtonMixin.jsx';
import { QBtn, QIcon, QTooltip } from 'quasar';
import './css/BasicButton.css';

export default defineComponent({
    name: 'BasicButton',
    mixins: [BaseComponent],
    components: {
        QBtn,
        QIcon,
        QTooltip,
    },
    data() {
        return {
            isDefaultEval: null,
            isdisabledeval: null,
            stopPropagationEval: null,
            isdisabledfailed: 1,
            isActive: false,
        form_changed_indicator: null,
        form_changed_parent: null,
    };
    },
    props: {},
    created() {
        if (this.controlData.HeaderControlType) {
            switch (this.controlData.HeaderControlType) {
                case 'SaveButton':
                    if (!this.controlData.Text)
                        this.controlData.Text = this.Translate('Save');
                    this.controlData.Color = 'primary';
                    let p = this.$parent;
                    while (p && !('cc_form_changed_indicator' in p))
                        p = p.$parent;

                    if (p) {
                        p.cc_form_changed_active = true;
                        p.cc_form_changed_button = this;
                        this.form_changed_parent = p;
                        this.form_changed_indicator = () => p.formChangedIndicator;
                    }
                    break;
                case 'RemoveButton':
                    if (!this.controlData.Text)
                        this.controlData.Text = this.Translate('Remove');
                    this.controlData.Color = 'danger';
                    break;
                case 'NewButton':
                    if (!this.controlData.Text)
                        this.controlData.Text = this.Translate('New');
                    break;
                case 'CloneButton':
                    if (!this.controlData.Text)
                        this.controlData.Text = this.Translate('Save as New');
                    break;
                case 'WizardButton':
                    if (!this.controlData.Text)
                        this.controlData.Text = this.Translate('Wizard');
                    break;
                case 'EditButton':
                    if (!this.controlData.Text)
                        this.controlData.Text = this.Translate('Edit');
                    break;
                case 'ExternalLinkButton':
                    if (!this.controlData.Text)
                        this.controlData.Text = this.Translate('New Tab');
                    break;
            }
        }
    },
    computed: {
        IsActive: {
            get() {
                return this.isActive;
            },
            set(value) {
                this.isActive = value;
            },
        },
        isDefault() {
            try {
                if (this.isDefaultEval === null && this.controlData.IsDefault)
                    this.isDefaultEval = utils.compile(this, this.controlData.IsDefault);
                if (this.isDefaultEval) return utils.evaluate(this.isDefaultEval, this);
                return false;
            } catch (e) {
                utils.warn('Icon could not evaluate expression: ' + this.controlData.IsDefault + '; ' + e);
                return false;
            }
        },
        isdisabled() {
            try {
                if (this.form_changed_parent) {
                    const modal = this.form_changed_parent;
                    const enabled = modal.cc_form_changed_active
                        && modal.cc_form_changed_button?.prerender_complete
                        && modal.cc_form_changed_form?.prerender_complete
                        && modal.cc_form_changed_indicator;
                    return !enabled;
                }

                if (this.form_changed_indicator)
                    return !this.form_changed_indicator();

                if (this.isdisabledeval === null && this.controlData.IsDisabled)
                    this.isdisabledeval = utils.compile(this, this.controlData.IsDisabled);

                if (this.isdisabledeval) {
                    const res = utils.evaluate(this.isdisabledeval, this, false, () => {
                        if (this.isdisabledfailed >= 0 && this.isdisabledfailed < 10)
                            setTimeout(() => this.isdisabledfailed++, 50 * this.isdisabledfailed);
                    });

                    return JSON.parse(res);
                }
                return false;
            } catch (e) {
                utils.warn('isdisabled could not evaluate expression: ' + this.controlData.IsDisabled + '; ' + e);
                return false;
            }
        },
        stopPropagation() {
            try {
                if (this.stopPropagationEval === null && this.controlData.StopPropagation)
                    this.stopPropagationEval = utils.compile(this, this.controlData.StopPropagation);
                if (this.stopPropagationEval) return utils.evaluate(this.stopPropagationEval, this);
                return false;
            } catch (e) {
                utils.warn('stopPropagation could not evaluate expression: ' + this.controlData.StopPropagation + '; ' + e);
                return false;
            }
        },
        styles() {
            return { 
                ...this.wrapperStyles, 
                ...this.finalButtonStyles 
            };
        },
        routedStyles() {
            // 1. Base wrapper styles
            const wrapperStyles = {
                ...this.sizeStyle,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
            };
            
            // 2. Base button classes and styles
            const buttonClasses = [this.buttonClasses]; // Array holding mapped themes/cssClass
            const buttonStyles = { ...this.buttonStyles }; // Object holding text align hints

            // 3. Get the fully resolved CSS object from your utility
            const resolvedStyles = utils.resolveStyleHints(this.styleHints, this) || {};

            // 4. Explicitly route these to the wrapper, bypassing the inner keywords
            // This prevents the generic 'align' keyword below from stealing 'align-self'.
            // 'padding' stays on the wrapper (Vue2 behavior) so it spaces the button
            // rather than squeezing the QBtn's flex-centered content off-center.
            const explicitWrapperKeywords = [
                'align-self', 'justify-self', 'margin', 'flex', 'padding'
            ];

            // 5. These keywords indicate CSS properties that belong ON the Quasar button
            const innerKeywords = [
                'border', 'radius', 'background', 'color', 
                'font', 'text', 'justify', 'align', 'line-height', 'cursor'
            ];

            // 6. Route each CSS property to the wrapper or the button
            // SizeOptions Fixed width must win on the wrapper (so HorizontalAlignChildren
            // can center). CustomStyle "width:100%" belongs on the QBtn so it fills that
            // Fixed shell — Vue2 WizardButton puts StyleHints on the v-btn the same way.
            const fixedWidthMode = this.sizeOptions?.Width?.Mode === 'Fixed';
            for (const [key, value] of Object.entries(resolvedStyles)) {
                const lowerKey = key.toLowerCase();
                
                if (explicitWrapperKeywords.some(keyword => lowerKey.includes(keyword))) {
                    wrapperStyles[key] = value;
                } else if (innerKeywords.some(keyword => lowerKey.includes(keyword))) {
                    buttonStyles[key] = value;
                } else if (lowerKey === 'width' && fixedWidthMode) {
                    buttonStyles[key] = value;
                } else {
                    // Visibility and everything else stay on the wrapper
                    wrapperStyles[key] = value;
                }
            }

            // Fixed Height: keep the old q-my-xs (4px) vertical nudge on the wrapper,
            // not the QBtn — avoids overflow clip / focus jump while preserving alignment
            // next to dense inputs (e.g. Bulk Create SourceFile folder button).
            if (this.sizeOptions?.Height?.Mode === 'Fixed') {
                const bump = (current) => {
                    if (current == null || current === '')
                        return '4px';
                    return `calc(${current} + 4px)`;
                };
                const mtKey = Object.keys(wrapperStyles).find(k => k.toLowerCase() === 'margin-top') || 'marginTop';
                const mbKey = Object.keys(wrapperStyles).find(k => k.toLowerCase() === 'margin-bottom') || 'marginBottom';
                wrapperStyles[mtKey] = bump(wrapperStyles[mtKey] ?? wrapperStyles.marginTop);
                wrapperStyles[mbKey] = bump(wrapperStyles[mbKey] ?? wrapperStyles.marginBottom);
            }

            return { wrapperStyles, buttonStyles, buttonClasses };
        },

        // Expose them cleanly for the render function
        wrapperStyles() {
            return this.routedStyles.wrapperStyles;
        },
        
        finalButtonStyles() {
            return this.routedStyles.buttonStyles;
        },

        finalButtonClasses() {
            return this.routedStyles.buttonClasses;
        }
    },
    methods: {
        async doClick(e) {
            e.cancelBubble = true;
            if (this.stopPropagation) e.stopPropagation();

            const blockConcurrent = this.controlData.BlockConcurrentClicks === true;
            if (blockConcurrent) {
                if (this.actionsRunning) return;
                this.actionsRunning = true;
            }
            try {
                await utils.executeAndCompileAllActions(this.controlData.Actions, null, this);
            } finally {
                if (blockConcurrent)
                    this.actionsRunning = false;
            }
        },
        preRenderComplete() {
            this.finishRenderHandler(this);
        },
        ToggleIsActive() {
            this.isActive = !this.isActive;
        },
    },
    render() {
        try {
            if (!this.todisplay) return null;

            let content = this.getButtonContent();

            let tooltip = this.renderButtonTooltip();

            let button = (
                <QBtn
                    class={this.finalButtonClasses}
                    style={this.finalButtonStyles}
                    color={this.finalButtonStyles.backgroundColor || this.finalButtonStyles.background ? undefined : this.color}
                    align={this.align}
                    fab={this.buttonFab}
                    round={this.buttonFab}
                    flat={this.qBtnFlat}
                    outline={this.qBtnOutline}
                    size={this.size}
                    noCaps
                    is-default={this.isDefault}
                    dense={this.density}
                    onClick={(e) => this.doClick(e)}
                    disable={this.isdisabled == 'true' || this.isdisabled == true}
                >
                    {content}
                    {tooltip}
                </QBtn>
            );

            //let finalContent = utils.generateTooltip(h, button, this.tooltip, this.tooltipLocation);

            return (
                <div
                    class={{
                        'c-BasicButton': true,
                        'c-useLiteralCasing': this.useLiteralCasing,
                        [`c-name-${this.name || 'unnamed'}`]: true,
                        // Popup menu rows skip the right gutter (menu-host CSS covers nested cases).
                        'q-mr-sm': this.parentType !== 'BasicPopupMenu'
                            && this.parentType !== 'IconPopupMenu',
                        'text-button': this.text,
                    }}
                    v-show={this.isvisible}
                    style={this.wrapperStyles}
                >
                    {button}
                </div>
            );
        } catch (e) {
            utils.error('BasicButton Render failed', e);
            return <div>Button Failed to Render {e}</div>;
        }
    },
});